import React from 'react';
import {
  DocPage, PageHeader, Section, Prerequisites,
  Para, Callout, NextStep,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './Customer.css';

const TOC = [
  { id: 'overview',   label: 'Overview' },
  { id: 'fields',     label: 'Customer fields' },
  { id: 'currency',   label: 'Base currency' },
  { id: 'addresses',  label: 'Billing & shipping addresses' },
];

// ── Customer card ─────────────────────────────────────────────────────────────

function CustomerCard({ c }) {
  return (
    <div className="cust-card">
      <div className="cust-card-header">
        <span className="cust-card-name">{c.name}</span>
        <span className="cust-card-tax">{c.taxIdLabel}: {c.taxId}</span>
      </div>
      <div className="cust-card-currency-row">
        <span className="cust-field-label">Base Currency</span>
        <span className="cust-currency-value">{c.currency} ({c.currencySymbol})</span>
      </div>
      <div className="cust-card-addresses">
        <AddressBlock label="Billing Address" address={c.billingAddress} />
        <AddressBlock label="Shipping Address" address={c.shippingAddress} />
      </div>
      <div className="cust-tax-note">
        <span className="cust-tax-note-icon">ℹ</span>
        <span>{c.taxNote}</span>
      </div>
    </div>
  );
}

function AddressBlock({ label, address }) {
  return (
    <div className="cust-address">
      <div className="cust-address-label">{label}</div>
      <div className="cust-address-line">{address.line1}</div>
      <div className="cust-address-line cust-address-line--city">
        {address.city}, {address.state}
      </div>
      <div className="cust-address-line">{address.country}</div>
    </div>
  );
}

// ── Currency → rate card diagram ──────────────────────────────────────────────

function CurrencyRateCardDiagram({ currencies, feeComponent }) {
  return (
    <div className="cust-currency-diagram">
      <div className="cust-currency-diagram-header">
        Rate card entries for {feeComponent}
      </div>
      <div className="cust-currency-rows">
        {currencies.map((entry) => (
          <div
            key={entry.currency}
            className={`cust-currency-row${entry.active ? ' cust-currency-row--active' : ''}`}
          >
            <span className={`cust-currency-tag${entry.active ? ' cust-currency-tag--active' : ''}`}>
              {entry.currency}
            </span>
            <span className="cust-currency-desc">{entry.note}</span>
            {entry.active
              ? <span className="cust-currency-badge">selected for this customer</span>
              : <span className="cust-currency-badge cust-currency-badge--dim">not applicable</span>
            }
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Customer({ onNavigate }) {
  const { industry } = useIndustry();
  const { customer: c, multiCurrency } = industry;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Customers & Contracts"
        title="Customer"
        description="A Customer is the entity invoices are issued to — it holds the name, address, tax IDs, and billing currency that every invoice requires."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'concepts',      label: 'Core Concepts' },
          { id: 'fee', label: 'Fees' },
          { id: 'rate-card',     label: 'Rate Cards' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          Every invoice in the system is issued to a customer. The customer record
          captures the legal and financial identity of the buyer — their registered
          name, tax number, the currency they are billed in, and the addresses
          that determine tax jurisdiction.
        </Para>
        <Para>
          Customers are independent of products. A single customer can be billed
          for usage across multiple products in the same billing cycle. Their base
          currency and addresses apply to every invoice generated for them.
        </Para>
      </Section>

      <Section id="fields" title="Customer fields">
        <Para>
          Here is an example customer record for {c.name}:
        </Para>
        <CustomerCard c={c} />
        <Para>
          <strong>Name</strong> is the legal name of the company. It appears on
          every invoice exactly as entered. For businesses, this should match the
          name on file with the tax authority.
        </Para>
        <Para>
          <strong>Tax Identifier</strong> ({c.taxIdLabel} in this example) is the
          registration number issued by the tax authority. It is printed on invoices
          and is required for compliant B2B invoicing in most jurisdictions.
        </Para>
      </Section>

      <Section id="currency" title="Base currency">
        <Para>
          The base currency determines which rate card entries are used when
          computing charges for this customer. Rate cards are scoped to a fee
          component, a tag combination, <em>and</em> a currency — so only the
          entries matching the customer's base currency are considered.
        </Para>
        <CurrencyRateCardDiagram
          currencies={c.rateCardCurrencies}
          feeComponent={multiCurrency.feeComponent}
        />
        <Para>
          In this example, {c.name} has a base currency of{' '}
          <strong>{c.currency}</strong>. When generating their invoice, the system
          selects only the {c.currency} rate card entries. Rate cards in other
          currencies are ignored entirely.
        </Para>
        <Callout type="tip">
          Rate cards for multiple currencies can coexist on the same fee
          and tag combination. The customer's base currency is the selector — not a
          filter applied at invoice time, but a fixed property of the customer record.
        </Callout>
      </Section>

      <Section id="addresses" title="Billing & shipping addresses">
        <Para>
          Both addresses are printed on the invoice. Beyond formatting, they carry
          a tax function: the billing address identifies the customer's registered
          legal entity, while the shipping address identifies where the goods or
          services are delivered.
        </Para>
        <Para>
          <strong>For Indian GST</strong>, the combination of the two addresses
          determines the tax split:
        </Para>
        <Para>
          If the billing state and shipping state are the <strong>same</strong>,
          the supply is intrastate and the invoice carries <strong>CGST + SGST</strong>.
          If they are <strong>different</strong>, the supply is interstate and the
          invoice carries <strong>IGST</strong> instead.
        </Para>
        <Callout type="info">
          {c.taxNote}
        </Callout>
        <Para>
          In non-GST contexts, addresses still allow the system to route invoices
          to the correct legal entity and apply regional tax rules automatically.
        </Para>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'contract', title: 'Contracts', desc: 'Tie this customer to fees and configure billing cycles, discounts, and guardrails.' },
        ]}
      />
    </DocPage>
  );
}
